import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import {
  type BaselineImportPreview,
  type MasterBaseline,
  type MasterBaselineUpdate,
  commitBaselineImport,
  createMasterBaseline,
  deleteMasterBaseline,
  deleteMasterBaselineGroup,
  listMasterBaselines,
  previewBaselineImport,
  renameMasterBaselineGroup,
  updateMasterBaseline,
} from '../../api/masterBaselines';
import { extractErrorMessage } from '../../api/errors';
import { RefreshButton } from '../../components/RefreshButton';
import { SearchableSelect } from '../../components/SearchableSelect';
import { SortHeader } from '../../components/SortHeader';
import { sortRows, useSortedRows, useTableSort } from '../../util/tableSort';

/**
 * Superadmin-only CRUD over `public.master_baselines` (ROADMAP #44).
 *
 * The platform-curated reference data the participant grid + tenant
 * Mastery Rate card pull from. Add new rows to introduce a new
 * cohort × procedure_group × metric × statistic; tenant admins then
 * pick up the new groups via the dropdown on their Procedures page.
 */

/** The row fields the grid can be sorted by (everything but id /
 *  created_at). */
type SortKey =
  | 'operator'
  | 'cohort'
  | 'procedure_group'
  | 'metric'
  | 'statistic'
  | 'value'
  | 'p_value'
  | 'significant'
  | 'direction'
  | 'modality'
  | 'unit'
  | 'sample_size'
  | 'source';

/** Side-by-side-only sort keys: one cohort's value on a pivoted line. */
type PivotValueKey = 'pr_value' | 'npr_value';
type AnySortKey = SortKey | PivotValueKey;

/** Case-insensitive alphabetical order for the form's suggestion lists. */
const alpha = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

const OPERATOR_OPTIONS = [
  { value: 'manual', label: '👤 Manual (a person)' },
  { value: 'robotic', label: '🤖 Robotic' },
] as const;
const STATISTIC_OPTIONS = [
  { value: 'mean', label: 'mean' },
  { value: 'median', label: 'median' },
  { value: 'min', label: 'min' },
  { value: 'max', label: 'max' },
] as const;
const DIRECTION_OPTIONS = [
  { value: 'higher', label: 'higher is better' },
  { value: 'lower', label: 'lower is better' },
  { value: 'none', label: 'no direction' },
] as const;
const MODALITY_OPTIONS = [
  { value: 'hand', label: 'hand sensor (motion)' },
  { value: 'eeg', label: 'EEG (cognitive)' },
  { value: 'both', label: 'both' },
] as const;

/** Header config — `key: null` is the non-sortable Actions column. */
const HEAD_CELLS: { key: SortKey | null; label: string; numeric?: boolean }[] =
  [
    { key: 'operator', label: 'Operator' },
    { key: 'cohort', label: 'Cohort' },
    { key: 'procedure_group', label: 'Group' },
    { key: 'metric', label: 'Metric' },
    { key: 'statistic', label: 'Stat' },
    { key: 'value', label: 'Value', numeric: true },
    { key: 'p_value', label: 'P', numeric: true },
    { key: 'significant', label: 'Sig' },
    { key: 'direction', label: 'Dir' },
    { key: 'modality', label: 'Sensor' },
    { key: 'unit', label: 'Unit' },
    { key: 'sample_size', label: 'N', numeric: true },
    { key: 'source', label: 'Source' },
    { key: null, label: 'Actions', numeric: true },
  ];

type CsvCell = string | number | boolean | null | undefined;

/** RFC-4180 cell escaping: quote when the value carries a comma, quote,
 *  or newline; double any embedded quotes. */
function csvCell(v: CsvCell): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Build a CSV blob from a header + rows and trigger a browser save via
 *  a transient object URL — same client-only pattern as the crash-report
 *  .txt download (no server round-trip). */
function downloadCsv(
  filename: string,
  header: string[],
  rows: CsvCell[][],
): void {
  const lines = [header, ...rows].map((r) => r.map(csvCell).join(','));
  const blob = new Blob([lines.join('\n') + '\n'], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari's download pipeline can read the blob first.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** One at-a-glance stat in the library's summary strip. */
function LibStat({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export function MasterBaselines({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const [rows, setRows] = useState<MasterBaseline[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MasterBaseline | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<MasterBaseline | null>(null);
  // Group management state.
  const [renamingGroup, setRenamingGroup] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<string | null>(null);
  const [groupOpBusy, setGroupOpBusy] = useState(false);

  // CSV import state.
  const [importOpen, setImportOpen] = useState(false);
  const [importCsv, setImportCsv] = useState('');
  const [importSource, setImportSource] = useState(
    'ACS 2023 High vs Low Performers',
  );
  const [importPreview, setImportPreview] =
    useState<BaselineImportPreview | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importDone, setImportDone] = useState<string | null>(null);

  // Form state.
  const [fOperator, setFOperator] = useState<'manual' | 'robotic'>('manual');
  const [fCohort, setFCohort] = useState('practice_ready');
  const [fGroup, setFGroup] = useState('');
  const [fMetric, setFMetric] = useState('');
  const [fStatistic, setFStatistic] =
    useState<'mean' | 'median' | 'min' | 'max'>('median');
  const [fValue, setFValue] = useState('');
  const [fPValue, setFPValue] = useState('');
  const [fSignificant, setFSignificant] = useState(true);
  const [fDirection, setFDirection] =
    useState<'higher' | 'lower' | 'none'>('higher');
  const [fModality, setFModality] =
    useState<'hand' | 'eeg' | 'both'>('hand');
  const [fUnit, setFUnit] = useState('');
  const [fSampleSize, setFSampleSize] = useState('');
  const [fSource, setFSource] = useState('');

  const reload = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try {
      setRows(await listMasterBaselines());
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load master baselines'));
      setRows([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const knownCohorts = useMemo(() => {
    const s = new Set<string>(['attending', 'other', 'practice_ready', 'not_practice_ready']);
    for (const r of rows ?? []) s.add(r.cohort);
    return Array.from(s).sort(alpha);
  }, [rows]);

  const knownGroups = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows ?? []) {
      if (r.procedure_group !== null) s.add(r.procedure_group);
    }
    return Array.from(s).sort(alpha);
  }, [rows]);

  /** Distinct metric names already in the catalogue — offered as
   *  suggestions in the add-row form (free entry still allowed). */
  const knownMetrics = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows ?? []) s.add(r.metric);
    return Array.from(s).sort(alpha);
  }, [rows]);

  /** Map group → row count for the management section. */
  const groupCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows ?? []) {
      if (r.procedure_group === null) continue;
      m.set(r.procedure_group, (m.get(r.procedure_group) ?? 0) + 1);
    }
    return m;
  }, [rows]);

  /** Significant (usable-discriminator) reference values — the ones that
   *  actually fire comparisons. Part of the library's at-a-glance state. */
  const significantCount = useMemo(
    () => (rows ?? []).filter((r) => r.significant).length,
    [rows],
  );

  // Column sorting (client-side — the table is a few dozen rows). Default to
  // grouping by procedure_group so the catalogue reads group-by-group; the
  // tiebreak chain below then orders each group by metric → statistic →
  // cohort for a stable, readable default.
  // One sort state shared by both views, so flipping between them keeps the
  // chosen order. The side-by-side view adds two keys of its own (each
  // cohort's value); in the flat view those fall back to sorting by `value`.
  const sort = useTableSort<AnySortKey>('procedure_group');
  const { orderBy, order } = sort;

  const sortedRows = useMemo(() => {
    if (rows === null) return null;
    const dir = order === 'asc' ? 1 : -1;
    // Compare one field; nulls always sink to the bottom (direction-agnostic).
    const cmp = (av: unknown, bv: unknown): number => {
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        return Number(av) - Number(bv);
      }
      return String(av).localeCompare(String(bv));
    };
    // After the active column, fall back through a fixed chain so rows within
    // an equal primary value land in a predictable group→metric order.
    const TIEBREAK: SortKey[] = [
      'procedure_group',
      'metric',
      'statistic',
      'cohort',
    ];
    const field: SortKey =
      orderBy === 'pr_value' || orderBy === 'npr_value' ? 'value' : orderBy;
    return [...rows].sort((a, b) => {
      const primary = cmp(a[field], b[field]) * dir;
      if (primary !== 0) return primary;
      for (const k of TIEBREAK) {
        if (k === field) continue;
        const t = cmp(a[k], b[k]);
        if (t !== 0) return t;
      }
      return 0;
    });
  }, [rows, orderBy, order]);

  // "Side by side" pivots the practice_ready / not_practice_ready rows for one
  // (group × metric × statistic) into a single line so the two cohorts read
  // next to each other. Storage stays one-row-per-cohort — this is view only.
  const [pivot, setPivot] = useState(true);

  const pivotedRows = useMemo(() => {
    if (sortedRows === null) return null;
    const byKey = new Map<
      string,
      {
        key: string;
        operator: string;
        procedure_group: string | null;
        metric: string;
        statistic: string;
        modality: string;
        unit: string | null;
        direction: string;
        significant: boolean;
        p_value: number | null;
        // cohort → its row (value + edit/delete target)
        cohorts: Map<string, MasterBaseline>;
      }
    >();
    for (const r of sortedRows) {
      // Operator is part of the key so a manual and a robotic reference for
      // the same metric stay on their own lines (never merged).
      const key = `${r.operator}|${r.procedure_group ?? ''}|${r.metric}|${r.statistic}`;
      let entry = byKey.get(key);
      if (!entry) {
        entry = {
          key,
          operator: r.operator,
          procedure_group: r.procedure_group,
          metric: r.metric,
          statistic: r.statistic,
          modality: r.modality,
          unit: r.unit,
          direction: r.direction,
          significant: r.significant,
          p_value: r.p_value,
          cohorts: new Map(),
        };
        byKey.set(key, entry);
      }
      entry.cohorts.set(r.cohort, r);
    }
    const entries = Array.from(byKey.values());
    // Lines inherit the row order for every shared field; only the
    // per-cohort value columns need a sort of their own.
    if (orderBy === 'pr_value' || orderBy === 'npr_value') {
      const cohort =
        orderBy === 'pr_value' ? 'practice_ready' : 'not_practice_ready';
      return sortRows(
        entries,
        (e) => e.cohorts.get(cohort)?.value ?? null,
        order,
      );
    }
    return entries;
  }, [sortedRows, orderBy, order]);

  // Inline value editing in side-by-side view — click a value to edit it in
  // place (the common tweak). The pencil still opens the full-field dialog.
  const [inlineEdit, setInlineEdit] =
    useState<{ id: string; value: string } | null>(null);
  const [inlineBusy, setInlineBusy] = useState(false);

  const saveInline = async () => {
    if (!inlineEdit) return;
    const raw = inlineEdit.value.trim();
    const num = raw === '' ? null : Number(raw);
    if (num !== null && !Number.isFinite(num)) {
      setError('Value must be a number or blank.');
      return;
    }
    const target = inlineEdit;
    setInlineBusy(true);
    try {
      await updateMasterBaseline(target.id, { value: num });
      // Patch the row in place so the edit shows without a full reload.
      setRows((prev) =>
        prev
          ? prev.map((r) => (r.id === target.id ? { ...r, value: num } : r))
          : prev,
      );
      setInlineEdit(null);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to save value'));
    } finally {
      setInlineBusy(false);
    }
  };

  // direction / significance / p-value / unit are identical for a metric's two
  // cohort rows, so a single edit patches every underlying row in the pair.
  const [sharedEdit, setSharedEdit] = useState<
    { key: string; field: 'p_value' | 'unit'; value: string } | null
  >(null);

  const patchPair = async (
    cohortRows: MasterBaseline[],
    patch: MasterBaselineUpdate,
  ) => {
    setInlineBusy(true);
    try {
      await Promise.all(
        cohortRows.map((r) => updateMasterBaseline(r.id, patch)),
      );
      const ids = new Set(cohortRows.map((r) => r.id));
      setRows((prev) =>
        prev
          ? prev.map((r) => (ids.has(r.id) ? { ...r, ...patch } : r))
          : prev,
      );
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to save'));
    } finally {
      setInlineBusy(false);
    }
  };

  const saveShared = async (cohortRows: MasterBaseline[]) => {
    if (!sharedEdit) return;
    const raw = sharedEdit.value.trim();
    let patch: MasterBaselineUpdate;
    if (sharedEdit.field === 'p_value') {
      const num = raw === '' ? null : Number(raw);
      if (num !== null && (!Number.isFinite(num) || num < 0 || num > 1)) {
        setError('P-value must be between 0 and 1, or blank.');
        return;
      }
      patch = { p_value: num };
    } else {
      patch = { unit: raw === '' ? null : raw };
    }
    await patchPair(cohortRows, patch);
    setSharedEdit(null);
  };

  const submitRename = async () => {
    if (!renamingGroup) return;
    setRenameError(null);
    const target = renameTo.trim();
    if (target === '' || target === renamingGroup) {
      setRenameError('Pick a different non-empty name.');
      return;
    }
    setGroupOpBusy(true);
    try {
      await renameMasterBaselineGroup(renamingGroup, target);
      setRenamingGroup(null);
      setRenameTo('');
      await reload();
    } catch (err: unknown) {
      setRenameError(extractErrorMessage(err, 'rename failed'));
    } finally {
      setGroupOpBusy(false);
    }
  };

  const confirmDeleteGroup = async () => {
    if (!deletingGroup) return;
    setGroupOpBusy(true);
    try {
      await deleteMasterBaselineGroup(deletingGroup);
      setDeletingGroup(null);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'group delete failed'));
    } finally {
      setGroupOpBusy(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFOperator('manual');
    setFCohort('practice_ready');
    setFGroup('');
    setFMetric('');
    setFStatistic('median');
    setFValue('');
    setFPValue('');
    setFSignificant(true);
    setFDirection('higher');
    setFModality('hand');
    setFUnit('');
    setFSampleSize('');
    setFSource('ACS 2023 High vs Low Performers');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (r: MasterBaseline) => {
    setEditing(r);
    setFOperator(r.operator === 'robotic' ? 'robotic' : 'manual');
    setFCohort(r.cohort);
    setFGroup(r.procedure_group ?? '');
    setFMetric(r.metric);
    setFStatistic(r.statistic);
    setFValue(r.value != null ? String(r.value) : '');
    setFPValue(r.p_value != null ? String(r.p_value) : '');
    setFSignificant(r.significant);
    setFDirection(r.direction);
    setFModality(r.modality);
    setFUnit(r.unit ?? '');
    setFSampleSize(r.sample_size != null ? String(r.sample_size) : '');
    setFSource(r.source ?? '');
    setFormError(null);
    setDialogOpen(true);
  };

  const submit = async () => {
    setFormError(null);
    // Value is optional now — a metric can be catalogued from its
    // p-value / direction even when no cohort value was established.
    const valueNum = fValue.trim() === '' ? null : Number(fValue);
    if (valueNum !== null && !Number.isFinite(valueNum)) {
      setFormError('Value must be a number or blank.');
      return;
    }
    const pNum = fPValue.trim() === '' ? null : Number(fPValue);
    if (pNum !== null && (!Number.isFinite(pNum) || pNum < 0 || pNum > 1)) {
      setFormError('P-value must be between 0 and 1, or blank.');
      return;
    }
    const sampleNum =
      fSampleSize.trim() === '' ? null : Number(fSampleSize);
    if (sampleNum !== null && (!Number.isInteger(sampleNum) || sampleNum < 1)) {
      setFormError('Sample size must be a positive integer or blank.');
      return;
    }
    setFormSubmitting(true);
    try {
      if (editing) {
        await updateMasterBaseline(editing.id, {
          value: valueNum,
          p_value: pNum,
          significant: fSignificant,
          direction: fDirection,
          modality: fModality,
          unit: fUnit.trim() === '' ? null : fUnit.trim(),
          sample_size: sampleNum,
          source: fSource.trim() === '' ? null : fSource.trim(),
        });
      } else {
        await createMasterBaseline({
          operator: fOperator,
          cohort: fCohort.trim(),
          procedure_group: fGroup.trim() === '' ? null : fGroup.trim(),
          metric: fMetric.trim(),
          statistic: fStatistic,
          value: valueNum,
          p_value: pNum,
          significant: fSignificant,
          direction: fDirection,
          modality: fModality,
          unit: fUnit.trim() === '' ? null : fUnit.trim(),
          sample_size: sampleNum,
          source: fSource.trim() === '' ? null : fSource.trim(),
        });
      }
      setDialogOpen(false);
      await reload();
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'failed to save'));
    } finally {
      setFormSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMasterBaseline(deleting.id);
      setDeleting(null);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'delete failed'));
    }
  };

  const openImport = () => {
    setImportCsv('');
    setImportPreview(null);
    setImportError(null);
    setImportDone(null);
    setImportOpen(true);
  };

  const doPreview = async () => {
    setImportError(null);
    setImportDone(null);
    setImportBusy(true);
    try {
      setImportPreview(
        await previewBaselineImport(importCsv, importSource.trim() || undefined),
      );
    } catch (err: unknown) {
      setImportPreview(null);
      setImportError(extractErrorMessage(err, 'preview failed'));
    } finally {
      setImportBusy(false);
    }
  };

  const doCommit = async () => {
    setImportError(null);
    setImportBusy(true);
    try {
      const res = await commitBaselineImport(
        importCsv,
        importSource.trim() || undefined,
      );
      setImportDone(
        `Imported: ${res.created} created, ${res.updated} updated, ` +
          `${res.procedures_created} new procedure(s).`,
      );
      setImportPreview(null);
      await reload();
    } catch (err: unknown) {
      setImportError(extractErrorMessage(err, 'import failed'));
    } finally {
      setImportBusy(false);
    }
  };

  // Export the catalogue as CSV, matching whichever view is active and
  // its current sort order. "Side by side" exports the pivoted layout
  // (practice_ready / not_practice_ready side by side per metric); "All
  // rows" exports the flat one-row-per-cohort layout with every field.
  const exportCsv = useCallback(() => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (pivot) {
      const header = [
        'operator',
        'procedure_group',
        'metric',
        'statistic',
        'sensor',
        'practice_ready',
        'not_practice_ready',
        'direction',
        'significant',
        'p_value',
        'unit',
      ];
      const out: CsvCell[][] = (pivotedRows ?? []).map((e) => [
        e.operator,
        e.procedure_group ?? '',
        e.metric,
        e.statistic,
        e.modality,
        e.cohorts.get('practice_ready')?.value ?? '',
        e.cohorts.get('not_practice_ready')?.value ?? '',
        e.direction,
        e.significant ? 'yes' : 'no',
        e.p_value ?? '',
        e.unit ?? '',
      ]);
      downloadCsv(`master-baselines-side-by-side-${stamp}.csv`, header, out);
    } else {
      const header = [
        'operator',
        'cohort',
        'procedure_group',
        'metric',
        'statistic',
        'value',
        'p_value',
        'significant',
        'direction',
        'modality',
        'unit',
        'sample_size',
        'source',
      ];
      const out: CsvCell[][] = (sortedRows ?? []).map((r) => [
        r.operator,
        r.cohort,
        r.procedure_group ?? '',
        r.metric,
        r.statistic,
        r.value ?? '',
        r.p_value ?? '',
        r.significant ? 'yes' : 'no',
        r.direction,
        r.modality,
        r.unit ?? '',
        r.sample_size ?? '',
        r.source ?? '',
      ]);
      downloadCsv(`master-baselines-all-rows-${stamp}.csv`, header, out);
    }
  }, [pivot, pivotedRows, sortedRows]);

  const toolbar = (
    <Stack direction="row" spacing={1} alignItems="center">
      <ToggleButtonGroup
        size="small"
        exclusive
        value={pivot ? 'pivot' : 'flat'}
        onChange={(_, v) => v !== null && setPivot(v === 'pivot')}
        aria-label="baseline view"
      >
        <ToggleButton value="pivot" aria-label="side by side">
          Side by side
        </ToggleButton>
        <ToggleButton value="flat" aria-label="all rows">
          All rows
        </ToggleButton>
      </ToggleButtonGroup>
      <RefreshButton onClick={reload} refreshing={refreshing} />
      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        onClick={exportCsv}
        disabled={!rows || rows.length === 0}
      >
        Export CSV
      </Button>
      <Button variant="outlined" onClick={openImport}>
        Import CSV
      </Button>
      <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
        Add row
      </Button>
    </Stack>
  );

  return (
    <Stack spacing={2}>
      {actionsSlot ? (
        createPortal(toolbar, actionsSlot)
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
        >
          {!embedded && (
            <Stack>
              <Typography variant="h4">Clinical Reference Library</Typography>
              <Typography color="text.secondary">
                The validated faculty / experienced-cohort reference values
                every participant is evaluated against (ROADMAP #44). Edits
                are platform-wide.
              </Typography>
            </Stack>
          )}
          {toolbar}
        </Stack>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Library state at a glance — the scientific model's size + how much of
          it is a usable discriminator. All computed from the loaded rows. */}
      {rows && rows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <LibStat label="Procedure groups" value={knownGroups.length} />
            <LibStat label="Reference values" value={rows.length} />
            <LibStat label="Metrics" value={knownMetrics.length} />
            <LibStat label="Significant" value={significantCount} />
          </Stack>
        </Paper>
      )}

      {/* Procedure groups — rename / delete across the catalogue +
          every tenant's procedures.procedure_group. Adding a group
          is implicit: create a row in the table below with a new
          procedure_group value. */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="h6">Procedure groups</Typography>
          <Typography variant="caption" color="text.secondary">
            Rename or delete a group across the catalogue AND every
            tenant's Procedures page in one operation. To add a new
            group, use "Add row" below with the new procedure_group
            value — it appears here automatically.
          </Typography>
          {knownGroups.length === 0 ? (
            <Typography color="text.disabled" variant="body2">
              No procedure-specific groups yet.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {knownGroups.map((g) => (
                <Paper
                  key={g}
                  variant="outlined"
                  sx={{
                    px: 1,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {g}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${groupCounts.get(g) ?? 0} row${(groupCounts.get(g) ?? 0) === 1 ? '' : 's'}`}
                  />
                  <Tooltip title="Rename group everywhere">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setRenamingGroup(g);
                        setRenameTo(g);
                        setRenameError(null);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete group (removes catalogue rows + nulls tenant mappings)">
                    <IconButton size="small" onClick={() => setDeletingGroup(g)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {rows === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No reference rows yet. Click "Add row" to start.
          </Typography>
        </Paper>
      ) : pivot ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortHeader label="Operator" col="operator" sort={sort} />
                <SortHeader label="Group" col="procedure_group" sort={sort} />
                <SortHeader label="Metric" col="metric" sort={sort} />
                <SortHeader label="Stat" col="statistic" sort={sort} />
                <SortHeader label="Sensor" col="modality" sort={sort} />
                <SortHeader
                  label="Practice-ready"
                  col="pr_value"
                  sort={sort}
                  align="right"
                />
                <SortHeader
                  label="Not practice-ready"
                  col="npr_value"
                  sort={sort}
                  align="right"
                />
                <SortHeader label="Dir" col="direction" sort={sort} />
                <SortHeader label="Sig" col="significant" sort={sort} />
                <SortHeader label="P" col="p_value" sort={sort} align="right" />
                <SortHeader label="Unit" col="unit" sort={sort} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(pivotedRows ?? []).map((e) => {
                const cohortRows = [...e.cohorts.values()];
                // Click-to-edit for a shared text/number field (p_value/unit).
                const sharedCell = (
                  field: 'p_value' | 'unit',
                  display: string,
                ) => {
                  if (sharedEdit?.key === e.key && sharedEdit.field === field) {
                    return (
                      <TextField
                        autoFocus
                        variant="standard"
                        size="small"
                        value={sharedEdit.value}
                        disabled={inlineBusy}
                        onChange={(ev) =>
                          setSharedEdit({
                            key: e.key,
                            field,
                            value: ev.target.value,
                          })
                        }
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') {
                            ev.preventDefault();
                            void saveShared(cohortRows);
                          } else if (ev.key === 'Escape') {
                            setSharedEdit(null);
                          }
                        }}
                        onBlur={() => void saveShared(cohortRows)}
                        sx={{ width: 80 }}
                        inputProps={{ 'aria-label': `${field} value` }}
                      />
                    );
                  }
                  return (
                    <Tooltip title={`Click to edit ${field}`}>
                      <Box
                        component="span"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          const cur =
                            field === 'p_value' ? e.p_value : e.unit;
                          setSharedEdit({
                            key: e.key,
                            field,
                            value: cur != null ? String(cur) : '',
                          });
                        }}
                        sx={{
                          cursor: 'pointer',
                          borderBottom: '1px dashed',
                          borderColor: 'divider',
                          px: 0.5,
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        {display}
                      </Box>
                    </Tooltip>
                  );
                };
                const valueCell = (cohort: string) => {
                  const row = e.cohorts.get(cohort);
                  if (!row) {
                    return (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        component="span"
                      >
                        —
                      </Typography>
                    );
                  }
                  if (inlineEdit?.id === row.id) {
                    return (
                      <TextField
                        autoFocus
                        variant="standard"
                        size="small"
                        value={inlineEdit.value}
                        disabled={inlineBusy}
                        onChange={(ev) =>
                          setInlineEdit({ id: row.id, value: ev.target.value })
                        }
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') {
                            ev.preventDefault();
                            void saveInline();
                          } else if (ev.key === 'Escape') {
                            setInlineEdit(null);
                          }
                        }}
                        onBlur={() => void saveInline()}
                        sx={{ width: 80 }}
                        inputProps={{
                          'aria-label': `${cohort} value`,
                          style: { textAlign: 'right' },
                        }}
                      />
                    );
                  }
                  return (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Tooltip title="Click to edit value">
                        <Box
                          component="span"
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            setInlineEdit({
                              id: row.id,
                              value: row.value != null ? String(row.value) : '',
                            })
                          }
                          sx={{
                            cursor: 'pointer',
                            borderBottom: '1px dashed',
                            borderColor: 'divider',
                            px: 0.5,
                            '&:hover': { borderColor: 'primary.main' },
                          }}
                        >
                          {row.value ?? '—'}
                        </Box>
                      </Tooltip>
                      <Tooltip title={`Edit all ${cohort} fields`}>
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  );
                };
                return (
                  <TableRow key={e.key} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        variant={e.operator === 'robotic' ? 'filled' : 'outlined'}
                        color={e.operator === 'robotic' ? 'info' : 'default'}
                        label={
                          e.operator === 'robotic'
                            ? '🤖 Robotic'
                            : '👤 Manual'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {e.procedure_group ?? (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          component="span"
                        >
                          (none)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{e.metric}</TableCell>
                    <TableCell>{e.statistic}</TableCell>
                    <TableCell>
                      <Select
                        variant="standard"
                        value={e.modality}
                        disabled={inlineBusy}
                        onChange={(ev) =>
                          void patchPair(cohortRows, {
                            modality: ev.target.value as
                              | 'hand'
                              | 'eeg'
                              | 'both',
                          })
                        }
                        sx={{ fontSize: '0.8rem' }}
                        inputProps={{ 'aria-label': 'sensor' }}
                      >
                        <MenuItem value="hand">hand</MenuItem>
                        <MenuItem value="eeg">eeg</MenuItem>
                        <MenuItem value="both">both</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="right">
                      {valueCell('practice_ready')}
                    </TableCell>
                    <TableCell align="right">
                      {valueCell('not_practice_ready')}
                    </TableCell>
                    <TableCell>
                      <Select
                        variant="standard"
                        value={e.direction}
                        disabled={inlineBusy}
                        onChange={(ev) =>
                          void patchPair(cohortRows, {
                            direction: ev.target.value as
                              | 'higher'
                              | 'lower'
                              | 'none',
                          })
                        }
                        sx={{ fontSize: '0.8rem' }}
                        inputProps={{ 'aria-label': 'direction' }}
                      >
                        <MenuItem value="higher">↑ higher</MenuItem>
                        <MenuItem value="lower">↓ lower</MenuItem>
                        <MenuItem value="none">— none</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Click to toggle significance">
                        <Chip
                          size="small"
                          label={e.significant ? 'yes' : 'no'}
                          color={e.significant ? 'success' : 'default'}
                          variant={e.significant ? 'filled' : 'outlined'}
                          disabled={inlineBusy}
                          onClick={() =>
                            void patchPair(cohortRows, {
                              significant: !e.significant,
                            })
                          }
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {sharedCell(
                        'p_value',
                        e.p_value != null ? String(e.p_value) : '—',
                      )}
                    </TableCell>
                    <TableCell>{sharedCell('unit', e.unit ?? '—')}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {HEAD_CELLS.map((h) =>
                  h.key === null ? (
                    <TableCell key={h.label} align={h.numeric ? 'right' : 'left'}>
                      {h.label}
                    </TableCell>
                  ) : (
                    <SortHeader
                      key={h.label}
                      label={h.label}
                      col={h.key}
                      sort={sort}
                      align={h.numeric ? 'right' : 'left'}
                    />
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {(sortedRows ?? []).map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      variant={r.operator === 'robotic' ? 'filled' : 'outlined'}
                      color={r.operator === 'robotic' ? 'info' : 'default'}
                      label={r.operator === 'robotic' ? '🤖 Robotic' : '👤 Manual'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.cohort} />
                  </TableCell>
                  <TableCell>
                    {r.procedure_group ?? (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        component="span"
                      >
                        (none)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{r.metric}</TableCell>
                  <TableCell>{r.statistic}</TableCell>
                  <TableCell align="right">{r.value ?? '—'}</TableCell>
                  <TableCell align="right">{r.p_value ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.significant ? 'yes' : 'no'}
                      color={r.significant ? 'success' : 'default'}
                      variant={r.significant ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {r.direction === 'higher'
                      ? '↑'
                      : r.direction === 'lower'
                        ? '↓'
                        : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={r.modality}
                      color={
                        r.modality === 'eeg'
                          ? 'secondary'
                          : r.modality === 'both'
                            ? 'info'
                            : 'primary'
                      }
                    />
                  </TableCell>
                  <TableCell>{r.unit ?? '—'}</TableCell>
                  <TableCell align="right">{r.sample_size ?? '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={r.source ?? ''} placement="top">
                      <span>{(r.source ?? '—').slice(0, 28)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit value / unit / source">
                      <IconButton size="small" onClick={() => openEdit(r)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete row">
                      <IconButton
                        size="small"
                        onClick={() => setDeleting(r)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => !formSubmitting && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editing ? 'Edit row' : 'Add reference row'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editing && (
              <Alert severity="info" variant="outlined">
                Operator, cohort, group, metric and statistic form the
                unique lookup key. Once saved they can't be changed —
                delete and recreate to move a row.
              </Alert>
            )}
            <SearchableSelect
              label="Operator"
              fullWidth
              disabled={!!editing}
              value={fOperator}
              onChange={setFOperator}
              options={[...OPERATOR_OPTIONS]}
            />
            <SearchableSelect
              label="Cohort"
              fullWidth
              disabled={!!editing}
              value={fCohort}
              onChange={setFCohort}
              options={knownCohorts.map((c) => ({ value: c, label: c }))}
            />
            <TextField
              size="small"
              label="Procedure group (blank for non-procedure-specific)"
              fullWidth
              disabled={!!editing}
              value={fGroup}
              onChange={(e) => setFGroup(e.target.value)}
              placeholder="e.g. lap_cholecystectomy"
              helperText={
                knownGroups.length > 0
                  ? `Existing: ${knownGroups.join(', ')}`
                  : 'No groups yet'
              }
            />
            <Autocomplete
              freeSolo
              size="small"
              fullWidth
              disabled={!!editing}
              options={knownMetrics}
              inputValue={fMetric}
              onInputChange={(_, v) => setFMetric(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Metric"
                  placeholder="e.g. focus / bimanual_dexterity_v1 / duration"
                  helperText="Pick an existing metric or type a new one."
                />
              )}
            />
            <SearchableSelect
              label="Statistic"
              fullWidth
              disabled={!!editing}
              value={fStatistic}
              onChange={setFStatistic}
              options={[...STATISTIC_OPTIONS]}
            />
            <TextField
              size="small"
              label="Value (blank if not established)"
              fullWidth
              value={fValue}
              onChange={(e) => setFValue(e.target.value)}
              helperText="Leave blank to catalogue a metric with no usable cohort value (only a trend / NS)."
            />
            <TextField
              size="small"
              label="P-value (0–1, blank for NS / no test)"
              fullWidth
              value={fPValue}
              onChange={(e) => setFPValue(e.target.value)}
            />
            <SearchableSelect
              label="Direction"
              fullWidth
              value={fDirection}
              onChange={setFDirection}
              options={[...DIRECTION_OPTIONS]}
            />
            <SearchableSelect
              label="Sensor"
              fullWidth
              value={fModality}
              onChange={setFModality}
              options={[...MODALITY_OPTIONS]}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={fSignificant}
                  onChange={(e) => setFSignificant(e.target.checked)}
                />
              }
              label="Significant — drives dashboard comparison + mastery rate"
            />
            <TextField
              size="small"
              label="Unit (display hint)"
              fullWidth
              value={fUnit}
              onChange={(e) => setFUnit(e.target.value)}
              placeholder="s / m / m_per_s / m3 / fraction / unitless"
            />
            <TextField
              size="small"
              label="Sample size (optional)"
              fullWidth
              value={fSampleSize}
              onChange={(e) => setFSampleSize(e.target.value)}
            />
            <TextField
              size="small"
              label="Source / citation"
              fullWidth
              value={fSource}
              onChange={(e) => setFSource(e.target.value)}
            />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={formSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submit()}
            disabled={formSubmitting}
          >
            {formSubmitting ? 'Saving…' : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename group */}
      <Dialog
        open={renamingGroup !== null}
        onClose={() => !groupOpBusy && setRenamingGroup(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename procedure group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Renaming{' '}
              <strong style={{ fontFamily: 'monospace' }}>
                {renamingGroup}
              </strong>{' '}
              updates every catalogue row + every tenant procedure
              that referenced it.
            </Typography>
            <TextField
              size="small"
              fullWidth
              autoFocus
              label="New name"
              value={renameTo}
              onChange={(e) => setRenameTo(e.target.value)}
            />
            {renameError && <Alert severity="error">{renameError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRenamingGroup(null)}
            disabled={groupOpBusy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={groupOpBusy}
            onClick={() => void submitRename()}
          >
            {groupOpBusy ? 'Saving…' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete group confirm */}
      <Dialog
        open={deletingGroup !== null}
        onClose={() => !groupOpBusy && setDeletingGroup(null)}
      >
        <DialogTitle>Delete procedure group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong style={{ fontFamily: 'monospace' }}>
              {deletingGroup}
            </strong>
            <br />
            Removes every catalogue row in this group AND clears
            <code>procedure_group</code> on every tenant procedure that
            referenced it (tenant rows aren't deleted — just the
            mapping). Faculty bars stop showing for the affected
            procedures until you re-tag them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingGroup(null)} disabled={groupOpBusy}>
            Cancel
          </Button>
          <Button
            color="error"
            disabled={groupOpBusy}
            onClick={() => void confirmDeleteGroup()}
          >
            {groupOpBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
      >
        <DialogTitle>Delete reference row?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleting && (
              <>
                <strong>
                  {deleting.cohort} · {deleting.procedure_group ?? '(any)'} ·{' '}
                  {deleting.metric} · {deleting.statistic}
                </strong>{' '}
                — value {deleting.value}
              </>
            )}
            <br />
            This is a hard delete. The Faculty bars for any procedure
            mapped to this row will go empty until you recreate it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" onClick={() => void confirmDelete()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV import — paste → preview → commit */}
      <Dialog
        open={importOpen}
        onClose={() => !importBusy && setImportOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import master baselines from CSV</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              Paste a study CSV. Both layouts are handled — the flat
              per-metric format (operator, procedure, metric, statistic,
              practice_ready, not_practice_ready, trend, …) and the legacy
              two-block layout. Metric names, units, p-values, and trend
              direction are normalised. Preview first; nothing is written
              until you commit. Re-importing updates rows in place.
            </DialogContentText>
            <TextField
              label="Source / citation"
              size="small"
              fullWidth
              value={importSource}
              onChange={(e) => setImportSource(e.target.value)}
            />
            <TextField
              label="CSV"
              multiline
              minRows={6}
              maxRows={14}
              fullWidth
              value={importCsv}
              onChange={(e) => {
                setImportCsv(e.target.value);
                setImportPreview(null);
                setImportDone(null);
              }}
              placeholder="operator,procedure,metric,statistic,practice_ready,not_practice_ready,trend,unit,sample_size,p_value,modality"
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
            />
            {importError && <Alert severity="error">{importError}</Alert>}
            {importDone && <Alert severity="success">{importDone}</Alert>}
            {importPreview && (
              <Alert severity="info" variant="outlined">
                <strong>{importPreview.row_count}</strong> rows ·{' '}
                <strong>{importPreview.new_procedures.length}</strong> new
                procedure(s)
                {importPreview.new_procedures.length > 0 && (
                  <>
                    {' '}
                    ({importPreview.new_procedures
                      .map((p) => p.display_name)
                      .join(', ')})
                  </>
                )}
                {importPreview.warnings.length > 0 && (
                  <Box component="ul" sx={{ m: 0.5, pl: 2.5 }}>
                    {importPreview.warnings.map((w) => (
                      <li key={w}>
                        <Typography variant="caption" color="warning.main">
                          {w}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
            {importPreview && importPreview.rows.length > 0 && (
              <ImportPreviewTable rows={importPreview.rows} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setImportOpen(false)}
            disabled={importBusy}
          >
            Close
          </Button>
          <Button
            onClick={() => void doPreview()}
            disabled={importBusy || importCsv.trim() === ''}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            onClick={() => void doCommit()}
            disabled={importBusy || importPreview === null}
          >
            {importBusy ? 'Working…' : 'Commit import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

type PlannedRow = BaselineImportPreview['rows'][number];
type IndexedPlannedRow = { r: PlannedRow; i: number };

// `row` (the CSV line order) has no header: it is the default, so the
// preview reads in the order the CSV was pasted until a column is clicked.
const IMPORT_SORT = {
  row: (x: IndexedPlannedRow) => x.i,
  cohort: (x: IndexedPlannedRow) => x.r.cohort,
  group: (x: IndexedPlannedRow) => x.r.procedure_group,
  metric: (x: IndexedPlannedRow) => x.r.metric,
  stat: (x: IndexedPlannedRow) => x.r.statistic,
  value: (x: IndexedPlannedRow) => x.r.value,
  sig: (x: IndexedPlannedRow) => x.r.significant,
  dir: (x: IndexedPlannedRow) => x.r.direction,
  sensor: (x: IndexedPlannedRow) => x.r.modality,
};

function ImportPreviewTable({ rows }: { rows: PlannedRow[] }) {
  const indexed = useMemo(() => rows.map((r, i) => ({ r, i })), [rows]);
  const { sort, sorted } = useSortedRows(indexed, IMPORT_SORT, 'row');
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <SortHeader label="Cohort" col="cohort" sort={sort} />
            <SortHeader label="Group" col="group" sort={sort} />
            <SortHeader label="Metric" col="metric" sort={sort} />
            <SortHeader label="Stat" col="stat" sort={sort} />
            <SortHeader label="Value" col="value" sort={sort} align="right" />
            <SortHeader label="Sig" col="sig" sort={sort} />
            <SortHeader label="Dir" col="dir" sort={sort} />
            <SortHeader label="Sensor" col="sensor" sort={sort} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(({ r, i }) => (
            <TableRow key={i}>
              <TableCell>{r.cohort}</TableCell>
              <TableCell>{r.procedure_group ?? '—'}</TableCell>
              <TableCell>{r.metric}</TableCell>
              <TableCell>{r.statistic}</TableCell>
              <TableCell align="right">{r.value ?? '—'}</TableCell>
              <TableCell>{r.significant ? 'yes' : 'no'}</TableCell>
              <TableCell>
                {r.direction === 'higher'
                  ? '↑'
                  : r.direction === 'lower'
                    ? '↓'
                    : '—'}
              </TableCell>
              <TableCell>{r.modality}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
